import React from "react";
import { Actividad, Lead } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Phone, Mail, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

export default function ActividadesList() {
  const queryClient = useQueryClient();

  const { data: actividades = [] } = useQuery({
    queryKey: ['actividades'],
    queryFn: () => Actividad.list({ completada: false }, { orderBy: 'fecha_programada', orderDirection: 'asc' }),
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: () => Lead.list(),
  });

  const completarMutation = useMutation({
    mutationFn: (id) => Actividad.update(id, { 
      completada: true, 
      fecha_completada: new Date().toISOString() 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });

  const getLeadNombre = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead?.nombre || "Lead desconocido";
  };

  const getLeadEmpresa = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead?.empresa;
  };

  // Agrupar actividades por período
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);
  
  const finSemana = new Date(hoy);
  finSemana.setDate(finSemana.getDate() + 7);
  
  const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);

  const gruposActividades = {
    atrasadas: [],
    hoy: [],
    manana: [],
    estaSemana: [],
    esteMes: [],
    futuro: []
  };

  actividades.forEach(act => {
    const fecha = new Date(act.fecha_programada);
    fecha.setHours(0, 0, 0, 0);

    if (fecha < hoy) {
      gruposActividades.atrasadas.push(act);
    } else if (fecha.getTime() === hoy.getTime()) {
      gruposActividades.hoy.push(act);
    } else if (fecha.getTime() === manana.getTime()) {
      gruposActividades.manana.push(act);
    } else if (fecha < finSemana) {
      gruposActividades.estaSemana.push(act);
    } else if (fecha <= finMes) {
      gruposActividades.esteMes.push(act);
    } else {
      gruposActividades.futuro.push(act);
    }
  });

  const formatHora = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatFechaDia = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
  };

  const renderActividad = (actividad) => (
    <Card 
      key={actividad.id} 
      className={`p-3 mb-2 hover:shadow-md transition-shadow ${
        new Date(actividad.fecha_programada) < new Date() ? 'border-red-300 bg-red-50 dark:bg-red-950 dark:border-red-800' : 'dark:bg-gray-800 dark:border-gray-700'
      }`}
    >
      <div className="flex items-center gap-3">
        <Checkbox 
          checked={false}
          onCheckedChange={() => completarMutation.mutate(actividad.id)}
          className="h-5 w-5"
        />
        
        <div className={`p-2 rounded-full ${actividad.tipo === 'llamada' ? 'bg-green-100 dark:bg-green-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
          {actividad.tipo === 'llamada' ? (
            <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          )}
        </div>

        <Link to={createPageUrl(`Lead?id=${actividad.lead_id}`)} className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-white truncate">
            {getLeadNombre(actividad.lead_id)}
          </div>
          {getLeadEmpresa(actividad.lead_id) && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {getLeadEmpresa(actividad.lead_id)}
            </div>
          )}
          {actividad.descripcion && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
              {actividad.descripcion}
            </div>
          )}
        </Link>

        <div className="text-right text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatHora(actividad.fecha_programada)}
          </div>
        </div>
      </div>
    </Card>
  );

  const renderGrupo = (titulo, actividadesGrupo, icono, colorClase) => {
    if (actividadesGrupo.length === 0) return null;
    
    return (
      <div className="mb-6">
        <div className={`flex items-center gap-2 mb-3 ${colorClase}`}>
          {icono}
          <h3 className="font-semibold text-sm uppercase tracking-wider">{titulo}</h3>
          <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
            {actividadesGrupo.length}
          </span>
        </div>
        {actividadesGrupo.map(renderActividad)}
      </div>
    );
  };

  const totalActividades = actividades.length;

  if (totalActividades === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <CheckCircle2 className="w-12 h-12 mb-3 text-green-500" />
        <p className="text-lg font-medium">¡Todo al día!</p>
        <p className="text-sm">No tienes actividades pendientes</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-200px)]">
      <div className="pr-4">
        {renderGrupo(
          "Atrasadas", 
          gruposActividades.atrasadas, 
          <AlertTriangle className="w-4 h-4" />,
          "text-red-600 dark:text-red-400"
        )}
        
        {renderGrupo(
          "Hoy", 
          gruposActividades.hoy, 
          <Clock className="w-4 h-4" />,
          "text-indigo-600 dark:text-indigo-400"
        )}
        
        {renderGrupo(
          "Mañana", 
          gruposActividades.manana, 
          <Clock className="w-4 h-4" />,
          "text-blue-600 dark:text-blue-400"
        )}
        
        {renderGrupo(
          "Esta semana", 
          gruposActividades.estaSemana, 
          <Clock className="w-4 h-4" />,
          "text-gray-600 dark:text-gray-400"
        )}
        
        {renderGrupo(
          "Este mes", 
          gruposActividades.esteMes, 
          <Clock className="w-4 h-4" />,
          "text-gray-500 dark:text-gray-500"
        )}
        
        {renderGrupo(
          "Próximamente", 
          gruposActividades.futuro, 
          <Clock className="w-4 h-4" />,
          "text-gray-400 dark:text-gray-600"
        )}
      </div>
    </ScrollArea>
  );
}